/**
 * Geometría de las cajas de texto de la plantilla de impresión de la acreditación.
 *
 * El PDF lo genera el backend Laravel (`/eventos/{id}/acreditaciones/imprimir`)
 * sobre una plantilla de tamaño FIJO. El front no puede agrandarla: lo único que
 * puede hacer es evitar que se guarde más texto del que entra.
 *
 * IMPORTANTE — LAS MEDIDAS ESTÁN EN EL SISTEMA DEL MODELO, NO EN píxeles de pantalla
 * -------------------------------------------------------------------------------
 * El ancho de la columna de texto de la plantilla y el tamaño de fuente están
 * relacionados por un ratio que **sí está validado contra el PDF**:
 *
 *   342 / 10 = 34,2  (ancho / tamaño de fuente)
 *
 * Ese ratio se verificó reproduciendo el corte de líneas exacto del PDF (4/4/4/2) y,
 * con letra chica, la altura real medida sobre la plantilla a 1,3 px. Como sólo
 * importa el ratio, todo se expresa en un sistema coherente con ancho 342 y fuente
 * 10 px: así el corte de líneas del modelo coincide con el de la impresión.
 *
 * CALIBRACIÓN DE `condiciones` (342 x 211)
 * ----------------------------------------
 * Captura del usuario con la caja dibujada 764 x 473 px. Perfil de píxeles de tinta:
 *
 *   líneas del texto de referencia ...... 14 (4 + 4 + 4 + 2)
 *   salto entre líneas ................. 12,98 px
 *   salto entre párrafos ............... 22,4 / 23,7 / 24,2 px (+10 px extra)
 *   total ocupado por la referencia ..... 211,29 px (entra justo)
 *
 *   14 líneas x 13 px + 3 huecos x 9,6 px = 210,78 px <= 211 ✓
 *
 * CALIBRACIÓN DE `acceso_zonas` y `observaciones` (342 x 48)
 * ----------------------------------------------------------
 * Dos capturas del usuario, con las etiquetas de la herramienta leídas dígito a
 * dígito sobre el bitmap (ASCII art, no a ojo):
 *
 *   acceso_zonas .... 353 x 42 layout px   (dibujado 444 x 55)
 *   observaciones ... 357 x 50 layout px   (dibujado 447 x 64)
 *
 * Con el ratio validado, la fuente de cada caja sale de su PROPIA anchura, así que
 * el resultado no depende del zoom de la captura:
 *
 *   acceso_zonas .... fuente 10,32 px, línea 13,42 px  ->  42 / 13,42 = 3,13 líneas
 *   observaciones ... fuente 10,44 px, línea 13,57 px  ->  50 / 13,57 = 3,68 líneas
 *
 * Las dos convergen en **3 líneas**, y la cuarta (52 px en el modelo) no entra en
 * ninguna. Por eso la caja se expresa como 342 x 48: la altura mayor de las dos
 * (50 px escalado a 342/357) para no bloquear de más, y sigue sin dar lugar a una
 * cuarta línea.
 *
 * OJO CON LA LECTURA DE LAS ETIQUETAS: en una revisión previa se anotó esta caja como
 * «444 x 55 a escala 1:1». Era falso: la herramienta reporta píxeles de *layout*
 * (`getBoundingClientRect`) y las capturas estaban a ~1,25x de zoom, así que 444 x 55
 * eran píxeles DIBUJADOS, no de layout. Con ese error la caja daba 4 líneas en vez de
 * 3 y habría dejado pasar una línea que se corta al imprimir.
 *
 * Las constantes no son arbitrarias. Si la plantilla del backend cambia, hay que
 * volver a medir. `tests/unit/composables/admin/events/acreditacionPrintBox.spec.js`
 * verifica que los números sigan cerrando.
 */

/**
 * Tipografía compartida por las cajas de la plantilla.
 *
 * Quill renderiza a 13 px / line-height 1.5, así que heredar su CSS daría una
 * medición inválida: hay que fijar la tipografía real de la plantilla.
 */
const PLANTILLA_TYPOGRAPHY = {
    /** Tamaño de fuente base (el de Quill es 13 px; la plantilla usa menos). */
    baseFontSize: 10,
    /**
     * Interlineado. Medido sobre la plantilla: el avance de línea es 12,98 px con
     * letra normal y 9,41 px con letra chica (escala 0,725x), o sea que SÍ escala
     * con el tamaño de fuente.
     */
    lineHeight: 1.3,
    /**
     * Separación entre párrafos, en **px fijos**. Medido sobre la plantilla: el
     * hueco extra es ~10,3 px tanto con letra normal como con letra chica, o sea
     * que NO escala con el tamaño de fuente. Estaba en `em` y al poner letra chica
     * el hueco se achicaba por debajo del real.
     *
     * Quill NO separa párrafos (`.ql-editor p { margin: 0 }`), pero la plantilla sí:
     * sin replicarlo, un contenido con muchos párrafos se mide más corto que en el PDF.
     *
     * 9,6 px es el valor que hace que el texto de referencia entre justo
     * (14 x 13 + 3 x 9,6 = 210,78).
     */
    paragraphSpacingPx: 9.6
};

/** Caja de las condiciones de uso. Da para 14 líneas. */
export const ACREDITACION_CONDICIONES_PRINT_BOX = Object.freeze({
    /** Ancho de la columna de texto de la plantilla. */
    width: 342,
    /** Alto de la caja: es el límite real. */
    maxHeight: 211,
    ...PLANTILLA_TYPOGRAPHY
});

/**
 * Caja compartida por `acceso_zonas` y `observaciones`.
 * Más baja que la de condiciones: da para 3 líneas, no 14.
 */
export const ACREDITACION_ZONAS_OBSERVACIONES_PRINT_BOX = Object.freeze({
    width: 342,
    maxHeight: 48,
    ...PLANTILLA_TYPOGRAPHY
});

/** Altura de una línea del modelo, en px. */
export const ACREDITACION_LINE_HEIGHT_PX =
    ACREDITACION_CONDICIONES_PRINT_BOX.baseFontSize *
    ACREDITACION_CONDICIONES_PRINT_BOX.lineHeight;
